import { MATERIALS } from '../model/materials'
import { diffractionRoutes, obstacleOf } from './diffraction'
import { dist, type Frame, makeFrame, prismContours, type ShapeInput, segmentChord, type V3 } from './geometry'
import { perimeter, signedArea } from './polygon'
import { type Candidate, reflectionCandidates, sceneReflectors, secondOrderCandidates } from './reflections'
import { coreShape, insideRoom, materialOf, type RoomInput, roomSurfaces, roomVolume, wallCrossings } from './room'

export const SPEED_OF_SOUND = 343
/** Early reflections kept per source (the strongest unobstructed ones); each costs one HRTF panner in the engine. */
export const MAX_EARLY = 16
/** Candidates whose occlusion is tested per source before giving up; keeps the worker cheap in object-heavy scenes. */
const EVAL_LIMIT = 64
/**
 * Without diffraction, a fully blocked path would be near-silent. Real obstacles are walked around, so the summed loss is soft-limited:
 * a single wall costs roughly its transmission loss, a forest of pillars saturates here instead of muting the source.
 */
const MAX_OBSTRUCTION_DB = 32
const MIN_REVERB_T = 0.15
const MAX_REVERB_T = 20
/** Real rooms leak and scatter: doors, seams, roughness. Added to every surface so bare concrete is not infinitely live. */
const BASELINE_ABSORPTION = 0.03
/** Longest reverb delay line (s). Megastructure echoes are sparse and long; the FDN buffer is sized to match. */
export const MAX_FDN_DELAY = 1.5
/** Share of the direct-path obstruction (in dB) that also applies to the reverb send. */
const TAIL_SHADOW = 0.6

export type EnclosureInput = RoomInput

export interface AcousticSceneInput {
  enclosure: EnclosureInput
  objects: ShapeInput[]
  listener: V3
  /** `size` is the source's reference radius in metres (level is constant inside it); defaults to a 1 m point. */
  sources: { id: string; position: V3; size?: number }[]
}

export interface PathInfo {
  kind: 'direct' | 'reflection' | 'diffraction'
  /** Stable id of the reflecting surface patch (reflections only). */
  id: string
  /** Human-readable surface name, e.g. "Slab +X" or "Outer wall". */
  label?: string
  /** False when the path does not exist; only valid paths are listed for reflections. */
  valid: boolean
  length: number
  delay: number
  /** Linear amplitude: distance, surface reflectivity, curvature and obstruction combined. */
  gain: number
  /** Lowpass corner in Hz: air absorption, surface and obstruction combined. */
  cutoff: number
  obstructionDb: number
  /** Where the sound seems to come from (the source itself, or the mirrored image of it). */
  apparent: V3
  /** Polyline for debug drawing: source → [reflection point] → listener. */
  points: V3[]
}

export interface TailInfo {
  gain: number
  delay: number
  /** Lowpass corner for the send: darkened when the source is shadowed by a solid. */
  cutoff: number
}

export interface SourceAcoustics {
  id: string
  direct: PathInfo
  /** The strongest unobstructed reflections, strongest first (at most MAX_EARLY). */
  early: PathInfo[]
  /** Reflection candidates that were geometrically valid but blocked by a solid. */
  blocked: number
  tail: TailInfo
}

export interface RoomAcoustics {
  volume: number
  surfaceArea: number
  meanFreePath: number
  rt60Low: number
  rt60High: number
  /** Distance at which direct and diffuse energy are equal. */
  criticalDistance: number
  /** FDN delay-line lengths in seconds. */
  delays: number[]
}

export interface AcousticsResult {
  rev: number
  room: RoomAcoustics
  sources: SourceAcoustics[]
}

// ---- Filters -------------------------------------------------------------------------------

/**
 * Lowpass corners combine as 1/fc⁴ adds. The playback filters are 2nd-order (Butterworth) biquads,
 * so every corner in this file is a 2nd-order corner.
 */
export function combineCutoffs(...cutoffs: number[]): number {
  let s = 0
  for (const c of cutoffs) s += 1 / c ** 4
  return s > 0 ? s ** -0.25 : 20000
}

/** Corner at which a 2nd-order Butterworth lowpass has gain `g` (0..1) at `f` Hz. */
function cutoffForGain(f: number, g: number): number {
  if (g >= 0.9999) return 20000
  if (g <= 0.01) return f / 10
  return f / (1 / (g * g) - 1) ** 0.25
}

/** Air absorption as a lowpass corner for a path of the given length. ~0.03 dB/m at 4 kHz, rising with f². */
export function airCutoff(length: number): number {
  const g4 = 10 ** ((-0.03 * length) / 20)
  // Down to 150 Hz: a source a kilometre away should sound dull and distant, not just quiet.
  return Math.min(20000, Math.max(150, cutoffForGain(4000, g4)))
}

/** Lowpass corner that reproduces the high/low reflectivity ratio of a surface. */
export function surfaceCutoff(absLow: number, absHigh: number): number {
  const rLow = Math.sqrt(1 - absLow)
  const rHigh = Math.sqrt(1 - absHigh)
  return Math.min(20000, Math.max(600, cutoffForGain(4000, rHigh / rLow)))
}

// ---- Obstruction -----------------------------------------------------------------------------

export interface Obstruction {
  db: number
  cutoff: number
  count: number
  /** The part of db caused by primitives. A room's own walls shade the direct path but not the shared reverberant field. */
  objectDb: number
}

export function obstruction(frames: Frame[], a: V3, b: V3, room?: RoomInput): Obstruction {
  let objectDb = 0
  const cutoffs: number[] = []
  let count = 0
  for (const f of frames) {
    const chord = segmentChord(f, a, b)
    if (chord <= 1e-6) continue
    const mat = MATERIALS[f.shape.material]
    objectDb += mat.transmissionDb * Math.min(1.5, Math.max(0.4, chord / 0.25))
    cutoffs.push(mat.transmissionCutoff)
    count++
  }
  // A polygon room's own walls (round the corner of an L, say) transmit like a wall of the room's material;
  // the sound that matters there arrives by diffraction round the corner.
  let wallDb = 0
  const crossed = room ? wallCrossings(room, a, b) : 0
  if (room && crossed > 0) {
    const mat = MATERIALS[materialOf(room, 'wall')]
    wallDb = crossed * mat.transmissionDb
    for (let i = 0; i < crossed; i++) cutoffs.push(mat.transmissionCutoff)
    count += crossed
  }
  const soft = (db: number) => MAX_OBSTRUCTION_DB * (1 - Math.exp(-db / MAX_OBSTRUCTION_DB))
  return {
    db: soft(objectDb + wallDb),
    cutoff: cutoffs.length ? combineCutoffs(...cutoffs) : 20000,
    count,
    objectDb: soft(objectDb),
  }
}
// ---- Room ------------------------------------------------------------------------------------

function shapeSurface(s: ShapeInput): number {
  const [x, y, z] = s.size.map(Math.abs) as V3
  if (s.kind === 'box') return 2 * (x * y + y * z + x * z)
  if (s.kind === 'prism') {
    const contours = prismContours(s)
    const area = Math.abs(signedArea(contours[0] ?? [])) - contours.slice(1).reduce((n, c) => n + Math.abs(signedArea(c)), 0)
    return contours.reduce((n, c) => n + perimeter(c), 0) * y + 2 * Math.max(0, area)
  }
  const r = (x + z) / 4
  const outer = 2 * Math.PI * r * r + 2 * Math.PI * r * y
  if (s.kind === 'cylinder') return outer
  // Tube: the bore adds an inner lateral surface (closed end plates already count in `outer`).
  const wall = Math.min(Math.max(s.wall ?? 1, 0.01), 0.98 * r)
  return outer + 2 * Math.PI * (r - wall) * y
}

/** Sabine reverberation times (two bands) + FDN delay set. Primitives contribute absorption area only. */
export function roomAcoustics(enclosure: EnclosureInput, objects: ShapeInput[] = []): RoomAcoustics {
  const [w, h, d] = enclosure.size
  const volume = roomVolume(enclosure)
  let surface = 0
  let aLow = 0
  let aHigh = 0
  for (const { face, area } of roomSurfaces(enclosure)) {
    const m = MATERIALS[materialOf(enclosure, face)]
    surface += area
    aLow += area * (m.absLow + BASELINE_ABSORPTION)
    aHigh += area * (m.absHigh + BASELINE_ABSORPTION)
  }
  for (const o of objects) {
    const area = shapeSurface(o)
    const m = MATERIALS[o.material]
    // Roughly half of a free-standing body's surface faces the diffuse field effectively; add scattering loss.
    aLow += 0.5 * area * Math.max(m.absLow, 0.04)
    aHigh += 0.5 * area * Math.max(m.absHigh, 0.06)
  }
  const airLow = 4 * 0.0003 * volume
  const airHigh = 4 * 0.012 * volume
  const clampT = (t: number) => Math.min(MAX_REVERB_T, Math.max(MIN_REVERB_T, t))
  const rt60Low = clampT((0.161 * volume) / (aLow + airLow))
  const rt60High = Math.min(rt60Low, clampT((0.161 * volume) / (aHigh + airHigh)))
  const rtMid = Math.sqrt(rt60Low * rt60High)
  const criticalDistance = Math.max(0.3, 0.0566 * Math.sqrt(volume / rtMid))

  // Delay lines follow the room's own round-trip times so a 100 m hall has widely separated echoes
  // while a small room is dense. Slight detuning avoids coincident delay lengths.
  const tx = (2 * w) / SPEED_OF_SOUND
  const ty = (2 * h) / SPEED_OF_SOUND
  const tz = (2 * d) / SPEED_OF_SOUND
  const raw = [
    tx * 0.97,
    tz * 0.91,
    ty * 1.0,
    ((tx + tz) / 2) * 1.03,
    ((tx + ty) / 2) * 0.95,
    ((tz + ty) / 2) * 1.07,
    Math.hypot(tx, tz) * 0.88,
    Math.hypot(tx, ty, tz) * 0.83,
  ]
  const delays = raw.map((t, i) => Math.min(MAX_FDN_DELAY, Math.max(0.017 + i * 0.0031, t)))

  return {
    volume,
    surfaceArea: surface,
    meanFreePath: surface > 0 ? (4 * volume) / surface : 0,
    rt60Low,
    rt60High,
    criticalDistance,
    delays,
  }
}

// ---- Paths -----------------------------------------------------------------------------------

function directPath(source: V3, listener: V3, obs: Obstruction, size: number): PathInfo {
  const length = dist(source, listener)
  const gain = (size / Math.max(length, size, 1)) * 10 ** (-obs.db / 20)
  return {
    kind: 'direct',
    id: 'direct',
    valid: true,
    length,
    delay: length / SPEED_OF_SOUND,
    gain,
    cutoff: combineCutoffs(airCutoff(length), obs.cutoff),
    obstructionDb: obs.db,
    apparent: source,
    points: [source, listener],
  }
}

/** Barrier attenuation (Kurze–Anderson) for path difference `delta` metres at `freq` Hz: 5 dB at grazing, rising with Fresnel number. */
export function barrierLossDb(delta: number, freq: number): number {
  const n = (2 * Math.max(delta, 0)) / (SPEED_OF_SOUND / freq)
  const v = Math.sqrt(2 * Math.PI * n)
  return Math.min(MAX_DIFFRACTION_DB, 5 + 20 * Math.log10(v < 1e-6 ? 1 : v / Math.tanh(v)))
}

const MAX_DIFFRACTION_DB = 40

/**
 * Diffracted arrivals for a blocked direct path: round the obstacle edges (one per side) and over the top. Bending is
 * easy for low frequencies and hard for highs, so each path is quieter and duller than the straight line would be.
 */
function diffractedPaths(obstacles: ReturnType<typeof obstacleOf>[], room: RoomInput, source: V3, listener: V3, size: number): PathInfo[] {
  const routes = diffractionRoutes({ obstacles, room, source, listener })
  return routes.map((r) => {
    const low = barrierLossDb(r.delta, 250)
    const high = barrierLossDb(r.delta, 4000)
    const shape = 10 ** (-(high - low) / 20) // high band relative to the low band
    const last = r.points[r.points.length - 1]!
    const toLast: V3 = [last[0] - listener[0], last[1] - listener[1], last[2] - listener[2]]
    const m = Math.hypot(...toLast) || 1
    return {
      kind: 'diffraction' as const,
      id: r.id,
      label: r.label,
      valid: true,
      length: r.length,
      delay: r.length / SPEED_OF_SOUND,
      gain: (size / Math.max(r.length, size, 1)) * 10 ** (-low / 20),
      cutoff: combineCutoffs(airCutoff(r.length), surfaceCutoff(0, 1 - shape * shape)),
      obstructionDb: low,
      apparent: [
        listener[0] + (toLast[0] / m) * r.length,
        listener[1] + (toLast[1] / m) * r.length,
        listener[2] + (toLast[2] / m) * r.length,
      ] as V3,
      points: [source, ...r.points, listener],
    }
  })
}

/**
 * The strongest unobstructed one- and two-bounce reflections off the room and every primitive.
 * Candidates are ranked by their unobstructed level and tested for occlusion in that order, so a scene with
 * a hundred primitives still costs only a few dozen segment tests per source.
 */
function earlyReflections(
  reflectors: ReturnType<typeof sceneReflectors>,
  frames: Frame[],
  room: RoomInput,
  source: V3,
  listener: V3,
  size: number,
  keep: number,
): { early: PathInfo[]; blocked: number } {
  if (!insideRoom(room, source) || !insideRoom(room, listener)) return { early: [], blocked: 0 }
  const candidates: Candidate[] = [
    ...reflectionCandidates(reflectors, source, listener),
    ...secondOrderCandidates(reflectors, source, listener),
  ]
  const ranked = candidates
    .map((c) => {
      const gain = (c.materials.reduce((g, m) => g * Math.sqrt(1 - MATERIALS[m].absLow), 1) * size * c.spread) / Math.max(c.length, size, 1)
      return { c, gain }
    })
    .sort((a, b) => b.gain - a.gain)

  const early: PathInfo[] = []
  let blocked = 0
  const nudge = (p: V3, n: V3): V3 => [p[0] + n[0] * 1e-3, p[1] + n[1] * 1e-3, p[2] + n[2] * 1e-3]
  for (const { c, gain } of ranked.slice(0, EVAL_LIMIT)) {
    if (early.length >= keep) break
    const joints = c.contacts.map((k) => nudge(k.point, k.normal))
    const legs: V3[] = [source, ...joints, listener]
    // The spec rejects blocked reflections rather than attenuating them.
    let clear = true
    for (let i = 0; i + 1 < legs.length && clear; i++) clear = obstruction(frames, legs[i]!, legs[i + 1]!, room).count === 0
    if (!clear) {
      blocked++
      continue
    }
    early.push({
      kind: 'reflection',
      id: c.id,
      label: c.label,
      valid: true,
      length: c.length,
      delay: c.length / SPEED_OF_SOUND,
      gain,
      cutoff: combineCutoffs(airCutoff(c.length), ...c.materials.map((m) => surfaceCutoff(MATERIALS[m].absLow, MATERIALS[m].absHigh))),
      obstructionDb: 0,
      apparent: c.apparent,
      points: [source, ...c.contacts.map((k) => k.point), listener],
    })
  }
  return { early, blocked }
}
/**
 * Tail send level. Diffuse-field energy relative to a 1 m direct signal is 1/dc²; the energy already carried by the
 * discrete early reflections is subtracted so the tail does not count them twice. Distance does not matter here:
 * the reverberant field is (to first order) the same everywhere in the room.
 */
export function tailGain(room: RoomAcoustics, early: PathInfo[], size = 1): number {
  // Diffuse power scales with the square of the source's reference level (`size` metres of 1/r reach).
  const diffuse = (size * size) / (room.criticalDistance * room.criticalDistance)
  let earlyEnergy = 0
  for (const e of early) if (e.valid) earlyEnergy += e.gain * e.gain
  return Math.sqrt(Math.max(diffuse * 0.25, diffuse - earlyEnergy))
}

export function computeAcoustics(scene: AcousticSceneInput, rev = 0): AcousticsResult {
  const objectFrames = scene.objects.map(makeFrame)
  const core = coreShape(scene.enclosure)
  // A ring room's core column blocks like any primitive (its reflections come from the room's own surface list).
  const frames = core ? [...objectFrames, makeFrame(core)] : objectFrames
  const obstacles = frames.map(obstacleOf)
  const reflectors = sceneReflectors(scene.enclosure, objectFrames)
  const room = roomAcoustics(scene.enclosure, scene.objects)
  const sources = scene.sources.map<SourceAcoustics>((s) => {
    const obs = obstruction(frames, s.position, scene.listener, scene.enclosure)
    const size = Math.max(0.1, s.size ?? 1)
    const direct = directPath(s.position, scene.listener, obs, size)
    // Diffraction first: when the direct path is blocked it is most of what you hear, so it claims audio chains first.
    const diffracted = obs.count > 0 ? diffractedPaths(obstacles, scene.enclosure, s.position, scene.listener, size) : []
    const { early: reflected, blocked } = earlyReflections(
      reflectors,
      frames,
      scene.enclosure,
      s.position,
      scene.listener,
      size,
      MAX_EARLY - diffracted.length,
    )
    const early = [...diffracted, ...reflected]
    return {
      id: s.id,
      direct,
      early,
      blocked,
      tail: {
        // A source shadowed by a solid also feeds less energy, and duller energy, into the room's diffuse field.
        gain: tailGain(room, early, size) * 10 ** ((-TAIL_SHADOW * obs.objectDb) / 20),
        delay: direct.delay,
        cutoff: obs.objectDb > 0.5 ? Math.min(20000, obs.cutoff * 2.5) : 20000,
      },
    }
  })
  return { rev, room, sources }
}
